import { useEffect, useRef, useState } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { giphyApi, type GiphyItemDTO } from '../../services/api'

interface Props {
  onSelect: (gif: GiphyItemDTO) => void
}

/**
 * Search-driven GIF picker backed by the server-side Giphy proxy
 * (api/giphy/{trending,search}). The Giphy API key never leaves the
 * server. On mount we show trending; typing into the search box
 * debounces and switches to /search.
 */
export default function GifPicker({ onSelect }: Props) {
  const [items, setItems] = useState<GiphyItemDTO[] | null>(null)
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const debounce = useRef<ReturnType<typeof setTimeout>>()

  const load = async (q: string) => {
    setError(null)
    try {
      const r = q.trim()
        ? await giphyApi.search(q.trim())
        : await giphyApi.trending()
      setItems(r.data)
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load GIFs'
      setError(msg)
      setItems([])
    }
  }

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => load(query), 300)
    return () => { if (debounce.current) clearTimeout(debounce.current) }
  }, [query])

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 pt-3 pb-2 shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search GIFs"
            className="w-full pl-9 pr-3 py-2 text-sm bg-muted rounded-lg outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2">
        {items === null ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-6">
            <p className="text-sm font-medium text-foreground">GIFs unavailable</p>
            <p className="text-xs mt-1 max-w-[260px] leading-relaxed">{error}</p>
            <p className="text-xs mt-2 max-w-[260px] leading-relaxed text-muted-foreground/80">
              Set <code className="px-1 py-0.5 rounded bg-muted text-[10px]">GIPHY_API_KEY</code> in
              <code className="ml-1 px-1 py-0.5 rounded bg-muted text-[10px]">apps/server/.env</code>.
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground mt-6">
            {query.trim() ? 'No GIFs found' : 'No trending GIFs'}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1.5 mt-1">
            {items.map((g) => (
              <button
                key={g.id}
                onClick={() => onSelect(g)}
                className="rounded overflow-hidden bg-muted hover:ring-2 hover:ring-primary transition relative"
                style={{ aspectRatio: g.previewWidth && g.previewHeight ? `${g.previewWidth} / ${g.previewHeight}` : '1 / 1' }}
                title={g.title}
              >
                <img
                  src={g.previewUrl}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground text-center">
        Powered by GIPHY
      </div>
    </div>
  )
}
